import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { Controller, useForm, type Control } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { api } from "../../../../services/api";
import { CadastroService } from "../../../../services/auth/cadastro";

// Definição dos tipos para cada schema
type CPFFormData = z.infer<typeof cpfSchema>;
type PersonalInfoFormData = z.infer<typeof personalInfoSchema>;
type AddressFormData = z.infer<typeof addressSchema>;

// Define validation schemas for each step
const cpfSchema = z.object({
  cpf: z
    .string()
    .min(11, "CPF precisa ter 11 dígitos")
    .max(14, "CPF inválido")
    .refine((val) => validateCPF(val), "CPF inválido"),
});

const personalInfoSchema = z
  .object({
    nome: z.string().min(3, "Nome precisa ter no mínimo 3 caracteres"),
    email: z.string().email("E-mail inválido"),
    telefone: z.string().min(10, "Telefone inválido").max(15, "Telefone inválido"),
    password: z.string().min(6, "Senha precisa ter no mínimo 6 caracteres"),
    confirmarSenha: z.string().min(6, "Confirmação de senha inválida"),
  })
  .refine((data) => data.password === data.confirmarSenha, {
    message: "Senhas não correspondem",
    path: ["confirmarSenha"],
  });

const addressSchema = z.object({
  cep: z.string().min(8, "CEP inválido").max(9, "CEP inválido"),
  logradouro: z.string().min(3, "Logradouro inválido"),
  numero: z.string().optional(),
  complemento: z.string().optional(),
  bairro: z.string().min(2, "Bairro inválido"),
  cidade: z.string().min(2, "Cidade inválida"),
  estado: z
    .string()
    .min(2, "Estado inválido")
    .max(2, "Use a sigla do estado (ex: SP)"),
});

// Interface para a resposta da API de CEP
interface ViaCEPResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  erro?: boolean;
}

// Interface para a resposta da API de validação de email e telefone
// true = já existe no banco, false = não existe (pode usar)
interface ValidateEmailTelefoneResponse {
  email: boolean;
  telefone: boolean;
}

// CPF validation helper function
function validateCPF(cpf: string): boolean {
  // Remove non-digits
  cpf = cpf.replace(/[^\d]/g, "");

  // Check if length is 11
  if (cpf.length !== 11) return false;

  // Check if all digits are the same
  if (/^(\d)\1+$/.test(cpf)) return false;

  // Validate first check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }

  let remainder = sum % 11;
  const checkDigit1 = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(cpf.charAt(9)) !== checkDigit1) return false;

  // Validate second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }

  remainder = sum % 11;
  const checkDigit2 = remainder < 2 ? 0 : 11 - remainder;

  return parseInt(cpf.charAt(10)) === checkDigit2;
}

// Tipo para as props do componente FormInput
interface FormInputProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  name: string;
  placeholder: string;
  type?: string;
  onBlur?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface CadastroTutorProps {
  onBack: () => void;
}

function CadastroTutor({ onBack }: CadastroTutorProps) {
  const [currentStep, setCurrentStep] = useState<number>(1);

  const [termoAceito, setTermoAceito] = useState<boolean>(false);

  const navigate = useNavigate();
  const cadastroService = new CadastroService();

  // Form for step 1 (CPF validation)
  const cpfForm = useForm<CPFFormData>({
    resolver: zodResolver(cpfSchema),
    defaultValues: {
      cpf: "",
    },
  });

  // Form for step 2 (Personal info)
  const personalInfoForm = useForm<PersonalInfoFormData>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      nome: "",
      email: "",
      telefone: "",
      password: "",
      confirmarSenha: "",
    },
  });

  // Form for step 3 (Address)
  const addressForm = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
    },
  });

  // React Query Mutations
  const validateCPFMutation = useMutation({
    mutationFn: (cpf: string) => cadastroService.validateCPF(cpf),
    onSuccess: (data) => {
      console.log(data);
      
      if (data) {
        alert("CPF já cadastrado. Por favor, faça login.");
      } else {
        setCurrentStep(2);
      }
    },
    onError: (error) => {
      console.error("Erro ao validar CPF:", error);
      alert("Erro ao validar CPF. Por favor, tente novamente.");
    },
  });

  // Validação de email e telefone
  const validateEmailTelefoneMutation = useMutation<
    ValidateEmailTelefoneResponse,
    Error,
    { email: string; telefone: string }
  >({
    mutationFn: async (data: { email: string; telefone: string }): Promise<ValidateEmailTelefoneResponse> => {
      return await cadastroService.TutorvalidateEmailTelefone(data.email, data.telefone);
    },
    onSuccess: (data) => {
      // Se retornar true, significa que já existe no banco
      if (!data.email && !data.telefone) {
        // Ambos são false (não existem), pode prosseguir
        setCurrentStep(3);
      } else {
        if (data.email) personalInfoForm.setError("email", { message: "E-mail já está em uso" });
        if (data.telefone) personalInfoForm.setError("telefone", { message: "Telefone já está em uso" });
      }
    },
    onError: (error) => {
      console.error("Erro ao validar email e telefone:", error);
      alert("Erro ao validar email e telefone. Por favor, tente novamente.");
    },
  });

  type TutorFormData = CPFFormData & PersonalInfoFormData & AddressFormData;

  const createTutorMutation = useMutation({
    mutationFn: (formData: TutorFormData) => {
      return cadastroService.createTutor({
        ...formData,
        cpf: formData.cpf.replace(/[^\d]/g, ""),
        telefone: formData.telefone.replace(/[^\d]/g, "")
      });
    },
    onSuccess: () => {
      alert("Sucesso: Cadastro realizado com sucesso!");
      navigate("/");
    },
    onError: (error) => {
      console.error("Erro ao cadastrar:", error);
      alert("Erro ao cadastrar. Por favor, tente novamente.");
    },
  });

  // CEP Query using React Query
  const fetchCEP = async (cep: string) => {
    if (!cep || cep.replace(/[^0-9]/g, "").length !== 8) {
      return null;
    }

    const cleanCep = cep.replace(/[^0-9]/g, "");
    const response = await api.get(`/buscar-cep/${cleanCep}?cidadeObrigatoria=true`)
    const data = response.data as ViaCEPResponse;
    
    return data;
  };

  const cepMutation = useMutation({
    mutationFn: fetchCEP,
    onSuccess: (data) => {
      if (data && !data.erro) {
        addressForm.setValue("logradouro", data.logradouro);
        addressForm.setValue("bairro", data.bairro);
        addressForm.setValue("cidade", data.localidade);
        addressForm.setValue("estado", data.uf);
      } else if (data?.erro) {
        alert("CEP não encontrado");
      }
    },
    onError: (error: unknown) => {
      // Verificar se é erro de conexão/rede (já tratado pelo interceptor)
      if (error && typeof error === 'object') {
        const axiosError = error as {
          code?: string;
          message?: string;
          response?: { data?: { error?: string } };
        };

        // Detectar erros de conexão (connection refused, network error, etc)
        const isConnectionError =
          !axiosError.response ||
          axiosError.code === "ECONNREFUSED" ||
          axiosError.code === "ERR_NETWORK" ||
          axiosError.code === "NETWORK_ERROR" ||
          axiosError.message?.toLowerCase().includes("network error") ||
          axiosError.message?.toLowerCase().includes("connection refused") ||
          axiosError.message?.toLowerCase().includes("failed to fetch");

        // Se for erro de conexão, não mostrar alert (já foi mostrado toast pelo interceptor)
        if (isConnectionError) {
          return;
        }

        // Para outros erros, mostrar mensagem específica
        const errorMessage =
          axiosError.response?.data?.error || "Erro ao buscar CEP";
        alert(errorMessage);
      } else {
        alert("Erro ao buscar CEP");
      }
    },
  });

  const handleValidateCPF = async (): Promise<void> => {
    const isValid = await cpfForm.trigger("cpf");

    if (!isValid) {
      alert("CPF inválido");
      return;
    }

    validateCPFMutation.mutate(cpfForm.getValues("cpf"));
  };

  // Handle personal info submission
  const handlePersonalInfoSubmit = async (
    data: PersonalInfoFormData
  ): Promise<void> => {
    // Validar email e telefone antes de prosseguir
    validateEmailTelefoneMutation.mutate({
      email: data.email,
      telefone: data.telefone
    });
  };

  // Handle address submission and advance to terms
  const handleAddressSubmit = async (): Promise<void> => {
    setCurrentStep(4);
  };

  // Handle final registration after accepting terms
  const handleFinalSubmit = async (): Promise<void> => {
    if (!termoAceito) {
      alert("Erro: Você precisa aceitar os termos para continuar.");
      return;
    }

    const formData = {
      cpf: cpfForm.getValues("cpf"),
      ...personalInfoForm.getValues(),
      ...addressForm.getValues(),
      tipo: "tutor"
    };

    createTutorMutation.mutate(formData);
  };

  // Handle CEP search
  const handleCEPSearch = async (): Promise<void> => {
    const cep = addressForm.getValues("cep").replace(/[^0-9]/g, "");
    if (cep.length !== 8) return;

    cepMutation.mutate(cep);
  };

  // Custom input component
  const FormInput: React.FC<FormInputProps> = ({
    control,
    name,
    placeholder,
    type = "text",
    onBlur = undefined,
    disabled = false,
    icon,
  }) => (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, onBlur: fieldOnBlur, value },
        fieldState: { error },
      }) => (
        <div className="mb-3 sm:mb-4">
          <div
            className={`flex items-center w-full bg-white rounded-full h-14 sm:h-16 px-3 sm:px-4 border ${
              error ? "border-red-500" : "border-gray-300"
            }`}
          >
            {icon && <div className="mr-2 sm:mr-3">{icon}</div>}
            <input
              disabled={disabled}
              className="flex-1 text-gray-800 text-sm sm:text-base outline-none"
              placeholder={placeholder}
              onChange={onChange}
              onBlur={() => {
                fieldOnBlur();
                if (onBlur) onBlur();
              }}
              value={value ?? ""}
              type={type}
              autoComplete="off"
              autoCorrect="off"
            />
          </div>
          {error && (
            <p className="text-red-500 text-xs ml-3 sm:ml-4 mt-1">
              {error.message}
            </p>
          )}
        </div>
      )}
    />
  );

  // Button with loading state
  const ActionButton: React.FC<{
    title: string;
    onPress: () => void;
    isLoading?: boolean;
    backgroundColor?: string;
    textColor?: string;
    width?: string;
  }> = ({
    title,
    onPress,
    isLoading = false,
    backgroundColor = "#1d71b8",
    textColor = "white",
    width = "full",
  }) => (
    <button
      className={`${width.includes('w-') ? width : `w-${width}`} rounded-full h-14 sm:h-16 flex items-center justify-center font-semibold text-base sm:text-lg ${
        isLoading ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"
      }`}
      style={{ backgroundColor, color: textColor }}
      onClick={onPress}
      disabled={isLoading}
    >
      {isLoading ? (
        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
      ) : (
        title
      )}
    </button>
  );

  // Progress bar
  const ProgressBar: React.FC = () => (
    <div className="mb-6 mt-2 px-2">
      <div className="flex justify-between mb-2">
        <span className="text-xs sm:text-sm text-gray-500 font-medium">
          Passo {currentStep} de 4
        </span>
        <span className="text-xs sm:text-sm text-blue-500 font-medium">
          {Math.round((currentStep / 4) * 100)}%
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-500 rounded-full transition-all duration-300"
          style={{ width: `${(currentStep / 4) * 100}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <ProgressBar />
      <div className="bg-white px-3 sm:px-6 py-4 rounded-2xl sm:rounded-3xl mx-2 sm:mx-0 w-full max-w-full">
        <h2 className="text-lg sm:text-xl font-semibold mb-2 text-gray-800">
          Cadastro de Tutor
        </h2>

        {currentStep === 1 && (
          <>
            <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base">
              Vamos começar com a validação do seu CPF
            </p>

            <FormInput
              control={cpfForm.control}
              name="cpf"
              placeholder="CPF (apenas números)"
              type="text"
            />

            <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-6 gap-3 sm:gap-4">
              <button
                className="w-full sm:w-1/2 bg-gray-200 rounded-full h-14 sm:h-16 flex items-center justify-center text-gray-700 font-semibold text-base sm:text-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
                onClick={onBack}
              >
                Voltar
              </button>

              <ActionButton
                title="Validar CPF"
                onPress={handleValidateCPF}
                isLoading={validateCPFMutation.isPending}
                backgroundColor="#1d71b8"
                width="full sm:w-1/2 order-1 sm:order-2"
              />
            </div>
          </>
        )}

        {currentStep === 2 && (
          <>
            <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base">
              Preencha seus dados pessoais
            </p>

            <FormInput
              control={personalInfoForm.control}
              name="nome"
              placeholder="Nome Completo"
            />

            <FormInput
              control={personalInfoForm.control}
              name="email"
              placeholder="E-mail"
              type="email"
            />

            <FormInput
              control={personalInfoForm.control}
              name="telefone"
              placeholder="Telefone"
              type="tel"
            />

            <FormInput
              control={personalInfoForm.control}
              name="password"
              placeholder="Senha"
              type="password"
            />

            <FormInput
              control={personalInfoForm.control}
              name="confirmarSenha"
              placeholder="Confirmar Senha"
              type="password"
            />

            <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-6 gap-3 sm:gap-4">
              <button
                className="w-full sm:w-1/2 bg-gray-200 rounded-full h-14 sm:h-16 flex items-center justify-center text-gray-700 font-semibold text-base sm:text-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
                onClick={() => setCurrentStep(1)}
              >
                Voltar
              </button>

              <ActionButton
                title="Próximo"
                onPress={personalInfoForm.handleSubmit(handlePersonalInfoSubmit)}
                isLoading={validateEmailTelefoneMutation.isPending}
                backgroundColor="#1d71b8"
                width="full sm:w-1/2 order-1 sm:order-2"
              />
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base">
              Preencha seu endereço completo
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-2 mb-4">
              <div className="w-full sm:flex-1">
                <FormInput
                  control={addressForm.control}
                  name="cep"
                  placeholder="CEP"
                  type="text"
                  onBlur={handleCEPSearch}
                />
              </div>
              <button
                className={`w-full sm:w-auto bg-gray-200 rounded-full h-14 sm:h-16 px-4 sm:px-6 flex items-center justify-center font-semibold hover:bg-gray-300 transition-colors text-sm sm:text-base ${
                  cepMutation.isPending ? "opacity-70 cursor-not-allowed" : ""
                }`}
                onClick={handleCEPSearch}
                disabled={cepMutation.isPending}
              >
                {cepMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                ) : (
                  <span className="text-gray-700">Buscar CEP</span>
                )}
              </button>
            </div>

            <FormInput
              control={addressForm.control}
              name="logradouro"
              placeholder="Logradouro"
            />

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="w-full sm:flex-1">
                <FormInput
                  control={addressForm.control}
                  name="numero"
                  placeholder="Número"
                  type="text"
                />
              </div>
              <div className="w-full sm:flex-1">
                <FormInput
                  control={addressForm.control}
                  name="complemento"
                  placeholder="Complemento"
                />
              </div>
            </div>

            <FormInput
              control={addressForm.control}
              name="bairro"
              placeholder="Bairro"
            />

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="w-full sm:flex-1">
                <FormInput
                  control={addressForm.control}
                  name="cidade"
                  disabled
                  placeholder="Cidade"
                />
              </div>
              <div className="w-full sm:w-24">
                <FormInput
                  control={addressForm.control}
                  name="estado"
                  placeholder="UF"
                  disabled
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-6 gap-3 sm:gap-4">
              <button
                className="w-full sm:w-1/2 bg-gray-200 rounded-full h-14 sm:h-16 flex items-center justify-center text-gray-700 font-semibold text-base sm:text-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
                onClick={() => setCurrentStep(2)}
              >
                Voltar
              </button>

              <ActionButton
                title="Próximo"
                onPress={addressForm.handleSubmit(handleAddressSubmit)}
                width="full sm:w-1/2 order-1 sm:order-2"
              />
            </div>
          </>
        )}

        {currentStep === 4 && (
          <>
            <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base">
              Termos de Uso e Políticas de Privacidade
            </p>

            <div 
              className="bg-gray-50 rounded-lg mb-4 sm:mb-6 p-3 sm:p-4 overflow-y-auto"
              style={{ maxHeight: '250px' }}
            >
              <div className="text-gray-700 text-xs sm:text-sm leading-5 sm:leading-6">
                <p className="font-bold mb-4">TERMOS DE USO</p>
                
                <p className="mb-4">
                  Ao utilizar nosso aplicativo de agendamento veterinário, você concorda com os seguintes termos:
                </p>
                
                <p className="font-semibold mb-2">1. Uso do Aplicativo</p>
                <p className="mb-4">
                  O aplicativo destina-se exclusivamente ao agendamento de consultas veterinárias. Você se compromete a fornecer informações verdadeiras e atualizadas.
                </p>
                
                <p className="font-semibold mb-2">2. Responsabilidades</p>
                <p className="mb-4">
                  Você é responsável por manter suas credenciais de acesso seguras e por todos os agendamentos realizados em sua conta.
                </p>
                
                <p className="font-semibold mb-2">3. Elegibilidade</p>
                <p className="mb-4">
                  Para utilizar o serviço, você deve residir em São Luís - MA, comprovado através do CEP cadastrado. No primeiro atendimento, será necessário apresentar comprovante de residência atualizado em seu nome ou de parente de 1º grau.
                </p>
                
                <p className="font-semibold mb-2">4. Documentação</p>
                <p className="mb-4">
                  Serão aceitos como comprovantes: Conta de Energia, Água, Telefone ou Fatura de Cartão de Crédito, emitidos nos últimos 90 dias. A não apresentação ou documentação fora dos critérios poderá resultar no bloqueio do cadastro.
                </p>
                
                <p className="font-semibold mb-2">5. Privacidade</p>
                <p className="mb-4">
                  Seus dados pessoais e dos seus pets serão tratados conforme nossa Política de Privacidade, respeitando a LGPD.
                </p>
                
                <p>
                  Ao aceitar estes termos, você concorda em receber notificações sobre seus agendamentos e confirma estar ciente das exigências de residência e documentação.
                </p>
              </div>
            </div>

            <button
              className="flex items-start sm:items-center mb-4 sm:mb-6 cursor-pointer"
              onClick={() => setTermoAceito(!termoAceito)}
            >
              <div
                className={`w-5 h-5 sm:w-6 sm:h-6 rounded border-2 mr-3 flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0 ${
                  termoAceito ? "bg-blue-500 border-blue-500" : "border-gray-300"
                }`}
              >
                {termoAceito && (
                  <span className="text-white font-bold text-xs sm:text-sm">✓</span>
                )}
              </div>
              <span className="text-gray-700 flex-1 text-xs sm:text-sm">
                Eu li e aceito os termos de uso e políticas de privacidade
              </span>
            </button>

            <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-6 gap-3 sm:gap-4">
              <button
                className="w-full sm:w-1/2 bg-gray-200 rounded-full h-14 sm:h-16 flex items-center justify-center text-gray-700 font-semibold text-base sm:text-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
                onClick={() => setCurrentStep(3)}
              >
                Voltar
              </button>

              <button
                className={`w-full sm:w-1/2 bg-blue-600 text-white rounded-full h-14 sm:h-16 flex items-center justify-center font-semibold text-base sm:text-lg order-1 sm:order-2 ${
                  !termoAceito || createTutorMutation.isPending ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
                } transition-colors`}
                onClick={handleFinalSubmit}
                disabled={!termoAceito || createTutorMutation.isPending}
              >
                {createTutorMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                ) : (
                  "Finalizar Cadastro"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CadastroTutor;